// RightPanel.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Tabs, TabList, TabPanels, Tab, TabPanel, useDisclosure, useToast, AlertDialog,
    AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
    ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Modal, Button, Grid, Select,
    FormLabel, Heading, Text, useColorMode
} from '@chakra-ui/react';
import axios from '../utils/axiosConfig';
import { mulaw } from 'alawmulaw';
import { RightPanelHeader, ChatGPTPromptsTab, ConfigurationTab, EmbedTab } from '../components/rightpanel/index';
import { useAssistant } from "../context/AssistantContext";
import CallSetupFields, {
    buildCallData,
    extractMustacheVariables,
    readStoredCallPhones,
    readStoredCallVariables,
    saveStoredCallPhones,
    saveStoredCallVariables
} from './rightpanel/CallSetupFields';
import LiveCallActivityTimeline from './LiveCallActivityTimeline';

const RightPanel = () => {
    const {
        // handleNewAssistant,
        selectedAssistant,
        // handleSubmit,
        handleDelete,
        // knowledgeBaseFiles,
        // tools,
        formState,
        // handleInputChange,
        // handleSliderChange,
        // handleCheckboxChange,
        // handleVoiceChange,
        handleVoiceSettingsChange,
        // handleUrlChange,
        // isSubmitting,
        // formErrors,
        // setFormState,
        // onDuplicate,
        // mcps
    } = useAssistant();
    const { colorMode } = useColorMode();
    const [activeTab, setActiveTab] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [phoneNumber, setPhoneNumber] = useState('');
    const [fromPhoneNumber, setFromPhoneNumber] = useState('');
    const [yourPhones, setYourPhones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assistantPhones, setAssistantPhones] = useState([]);
    const [voiceLoading, setVoiceLoading] = useState(false);
    const [voices, setVoices] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const toast = useToast();
    const cancelRef = useRef();
    const voiceRef = useRef(null);
    const modelRef = useRef(null);
    const audioBufferQueue = useRef([]);
    const audioContext = useRef(new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 8000,
    }));
    const [isPlaying, setIsPlaying] = useState(false);
    const [socket, setSocket] = useState(null);
    const [liveActivityUrl, setLiveActivityUrl] = useState('');
    const [showLiveActivity, setShowLiveActivity] = useState(false);

    const isRealtimeModel = (model = '') => {
        return [
            'realtime:',
            'realtime-audio:',
            'realtime-text-audio:',
            'realtime-audio-text:'
        ].some(prefix => model.startsWith(prefix));
    };

    const callVariables = useMemo(() => {
        return extractMustacheVariables(formState.firstMessage, formState.systemPrompt);
    }, [formState.firstMessage, formState.systemPrompt]);

    const handleVariableValueChange = (variable, value) => {
        setVariableValues(prev => {
            const nextValues = {
                ...prev,
                [variable]: value
            };

            if (selectedAssistant?._id) {
                saveStoredCallVariables(selectedAssistant._id, nextValues);
            }

            return nextValues;
        });
    };

    const persistCallPhoneNumbers = (updates) => {
        if (!selectedAssistant?._id) return;

        const nextValues = {
            ...readStoredCallPhones(selectedAssistant._id),
            ...updates
        };

        saveStoredCallPhones(selectedAssistant._id, nextValues);
    };

    const handleFromPhoneNumberChange = (value) => {
        setFromPhoneNumber(value);
        persistCallPhoneNumbers({ from: value });
    };

    const handleToPhoneNumberChange = (value) => {
        setPhoneNumber(value);
        persistCallPhoneNumbers({ to: value });
    };

    useEffect(() => {
        const updateVoiceSettings = async () => {
            if (selectedAssistant) {
                try {
                    // First try to use cached voices
                    const cachedVoices = localStorage.getItem('ai_voices');
                    let currentVoices = [];

                    if (cachedVoices) {
                        currentVoices = JSON.parse(cachedVoices);
                        setVoices(currentVoices);

                        // Update form state with cached data first
                        const voiceMatch = currentVoices.find(v => v.id === selectedAssistant.cartesiaVoiceId);
                        console.log('voiceMatch', voiceMatch);
                        const provider = voiceMatch?.provider || '';
                        console.log('provider', provider);
                        handleVoiceSettingsChange(provider, selectedAssistant.cartesiaVoiceId);
                        console.log('2. formstate.voiceProvider', formState.voiceProvider);
                    } else {
                        // Then fetch fresh data in the background
                        const response = await axios.get('/api/protected/voices');
                        const fetchedVoices = response.data;

                        // Only update if the data has changed
                        if (JSON.stringify(fetchedVoices) !== JSON.stringify(currentVoices)) {
                            setVoices(fetchedVoices);
                            localStorage.setItem('ai_voices', JSON.stringify(fetchedVoices));

                            // Update form state with fresh data if needed
                            const voiceMatch = fetchedVoices.find(v => v.id === selectedAssistant.cartesiaVoiceId);
                            const provider = voiceMatch?.provider || 'OpenAI';
                            handleVoiceSettingsChange(provider, selectedAssistant.cartesiaVoiceId);
                        }
                    }


                } catch (err) {
                    console.error('Error updating voice settings:', err);
                }
            }
        };

        updateVoiceSettings();
    }, [selectedAssistant]);

    // Fetch available voices
    const fetchVoices = async (force = false) => {
        try {
            const cachedVoices = localStorage.getItem('ai_voices');
            if (cachedVoices && !force) {
                setVoices(JSON.parse(cachedVoices));
            } else {
                setVoiceLoading(true);
                const response = await axios.get('/api/protected/voices');
                const fetchedVoices = response.data;
                setVoices(fetchedVoices);
                localStorage.setItem('ai_voices', JSON.stringify(fetchedVoices));
            }
        } catch (err) {
            console.error('Error fetching voices:', err);
        } finally {
            setVoiceLoading(false);
        }
    };

    // Modal actions
    const handleOpenModal = async () => {
        try {
            const response = await axios.get('/api/protected/phone-numbers');
            // Ensure we're setting an array
            console.log(response.data);
            const phones = Array.isArray(response.data) ? response.data : [];

            setAssistantPhones(phones);

            if (phones.length > 0) {
                const storedPhones = readStoredCallPhones(selectedAssistant?._id);
                const storedFrom = phones.find(phone => phone.phoneNumber === storedPhones.from);
                setFromPhoneNumber(storedFrom ? storedPhones.from : phones[0].phoneNumber);
            } else {
                toast({
                    title: 'No phone number found',
                    description: 'Please go to Phone Number page and click the refresh button to fetch the phone numbers',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                return; // Don't open modal if no phones available
            }

            const response2 = await axios.get('/api/protected/settings/phone-numbers');
            console.log(response2.data);
            const userPhones = Array.isArray(response2.data.data) ? response2.data.data : [];
            setYourPhones(userPhones);

            if (userPhones.length > 0) {
                const storedPhones = readStoredCallPhones(selectedAssistant?._id);
                const storedTo = userPhones.find(phone => `${phone.countryCode}${phone.phoneNumber}` === storedPhones.to);
                setPhoneNumber(storedTo ? storedPhones.to : userPhones[0].countryCode + userPhones[0].phoneNumber);
            } else {
                toast({
                    title: 'No phone numbers found',
                    description: 'Please add your phone number under Settings > Your Phone Numbers',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                return; // Don't open modal if no phones available
            }

            const storedVariables = readStoredCallVariables(selectedAssistant?._id);

            setVariableValues(
                callVariables.reduce((values, variable) => ({
                    ...values,
                    [variable]: storedVariables[variable] ?? variableValues[variable] ?? ''
                }), {})
            );

            onOpen(); // Only open modal if we have both assistant and user phone numbers
        } catch (error) {
            console.error('Error fetching phone numbers:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch phone numbers. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleCall = async () => {
        setLoading(true);
        try {
            const apiKey = "";
            const response = await axios.post('/api/protected/outboundcall',
                {
                    from: fromPhoneNumber,
                    to: phoneNumber,
                    assistantId: selectedAssistant._id,
                    data: buildCallData(variableValues),
                    debug: {
                        liveActivity: true
                    },
                },
                {
                    headers: {
                        'authorization': `ApiKey ${apiKey}`
                    }
                }
            );
            console.log(response.data);
            const message = response.data.message;
            const callSid = response.data.callSid;
            const nextLiveActivityUrl = response.data.liveActivityUrl;
            toast({
                title: 'Call Initiated',
                description: `You will receive a call shortly. ${message} ${callSid}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            if (isRealtimeModel(formState.model) && nextLiveActivityUrl) {
                setLiveActivityUrl(nextLiveActivityUrl);
                setShowLiveActivity(true);
            } else {
                onClose(); // Close the modal after successful call
            }
        } catch (error) {
            console.error('Error initiating call:', error);
            toast({
                title: 'Call Failed',
                description: error.response?.data?.message || 'Failed to initiate call. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
            setIsDialogOpen(false);
        }
    };

    const handleCallModalClose = () => {
        setVariableValues({});
        setLiveActivityUrl('');
        setShowLiveActivity(false);
        onClose();
    };

    const handleVoiceRefresh = () => {
        fetchVoices(true);
    };

    const connectAssistant = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            console.log('Microphone access granted.');

            if (audioContext.current.state === 'suspended') {
                await audioContext.current.resume();
                console.log('AudioContext resumed.');
            }

            const newSocket = new WebSocket(`wss://aeb1-103-252-200-95.ngrok-free.app/connection`);
            setSocket(newSocket);

            newSocket.onmessage = (message) => {
                try {
                    const data = JSON.parse(message.data);
                    console.log('WebSocket message received', data);
                    const event = data.event;
                    if (event === 'media') {
                        if (data.media.payload) {
                            const binaryString = atob(data.media.payload); // Decode Base64 to binary string
                            const len = binaryString.length;
                            const bytes = new Uint8Array(len);
                            for (let i = 0; i < len; i++) {
                                bytes[i] = binaryString.charCodeAt(i);
                            }
                            const uint8arr = bytes;
                            audioBufferQueue.current.push(...uint8arr);
                            playAudioChunks();
                        }
                    } else if (event === 'mark') {
                        if (!isPlaying) playAudioChunks();
                    } else if (event === 'clear') {
                        // stop audio playing
                    }
                } catch (error) {
                    console.error(error);
                }
            };

            newSocket.onclose = () => {
                console.log('WebSocket closed');
            };

            newSocket.onopen = async () => {
                try {
                    console.log('WebSocket connected');
                    const response = await axios.post('/api/protected/webcall');
                    const { token } = response.data || {};
                    console.log(token);
                    newSocket.send(JSON.stringify({
                        event: 'start',
                        start: {
                            customParameters: {
                                jwt: token
                            },
                            streamSid: "123",
                            callSid: "123"
                        }
                    }));
                } catch (error) {
                    console.error(error);
                }
            };

            try {
                await audioContext.current.audioWorklet.addModule('./processor.js');
            } catch (error) {
                console.log(error);
            }
            const source = audioContext.current.createMediaStreamSource(mediaStream);
            const micProcessor = new AudioWorkletNode(audioContext.current, 'mic-processor');

            micProcessor.port.onmessage = (event) => {
                const int16Data = float32ToInt16(event.data);
                const muLawData = mulaw.encode(int16Data); // Encode to μ-law
                const base64Payload = btoa(String.fromCharCode(...muLawData)); // Base64-encode

                if (newSocket.readyState === WebSocket.OPEN) {
                    newSocket.send(JSON.stringify({
                        event: "media",
                        media: {
                            payload: base64Payload
                        }
                    })); // Send to backend
                }
            };

            source.connect(micProcessor);
        } catch (error) {
            console.error('Microphone access denied.', error);
        }
    };

    // Convert Float32 to Int16 for sending audio
    const float32ToInt16 = (float32Array) => {
        const int16Array = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
            int16Array[i] = Math.min(1, float32Array[i]) * 32767;
        }
        return int16Array;
    };

    // Playback function for queued audio chunks
    const playAudioChunks = async () => {
        if (audioBufferQueue.current.length === 0) {
            console.log('empty audioBufferQueue');
            setIsPlaying(false);
            return;
        }

        setIsPlaying(true);
        console.log(audioBufferQueue);

        const pcmSamples = mulaw.decode(Uint8Array.from(audioBufferQueue.current));
        const float32Samples = Float32Array.from(pcmSamples, (x) => x / 32768);
        console.log(float32Samples);

        const audioBuffer = await pcmToAudioBuffer(float32Samples);

        const source = audioContext.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.current.destination);
        source.start();
        source.onended = () => setIsPlaying(false);
        audioBufferQueue.current = [];
    };

    // Convert PCM to AudioBuffer
    const pcmToAudioBuffer = async (pcmChunk) => {
        console.log(`Received audio chunk of length: ${pcmChunk.length}`);
        const buffer = audioContext.current.createBuffer(1, pcmChunk.length, 8000);
        buffer.copyToChannel(pcmChunk, 0);
        return buffer;
    };



    return (
        <Box width="100%" bg={colorMode === 'light' ? 'gray.50' : 'gray.800'} p={6}>
            <RightPanelHeader
                // selectedAssistant={selectedAssistant}
                handleOpen={handleOpenModal}
            // handleSave={handleSubmit}
            // onNewAssistant={handleNewAssistant}
            // isSubmitting={isSubmitting}
            // setIsDialogOpen={setIsDialogOpen}
            // onDuplicate={onDuplicate}
            />

            {selectedAssistant && (
                <Tabs index={activeTab} onChange={(index) => setActiveTab(index)}>
                    <TabList>
                        <Tab color={colorMode === 'light' ? 'gray.800' : 'white'}>ChatGPT Prompts</Tab>
                        <Tab color={colorMode === 'light' ? 'gray.800' : 'white'}>Configuration</Tab>
                        {/* <Tab color={colorMode === 'light' ? 'gray.800' : 'white'}>Embed</Tab> */}
                    </TabList>
                    <TabPanels>
                        <TabPanel>
                            <Heading size="sm" mb={4}>ChatGPT Model Settings</Heading>
                            <ChatGPTPromptsTab
                                // formState={formState}
                                // handleInputChange={handleInputChange}
                                // handleSliderChange={handleSliderChange}
                                // knowledgeBaseFiles={knowledgeBaseFiles}
                                // tools={tools}
                                // colorMode={colorMode}
                                // setFormState={setFormState}
                                modelRef={modelRef}
                            // mcps={mcps}
                            />
                        </TabPanel>

                        <TabPanel>
                            <ConfigurationTab
                                // formState={formState}
                                // handleInputChange={handleInputChange}
                                // handleSliderChange={handleSliderChange}
                                handleVoiceRefresh={handleVoiceRefresh}
                                // handleCheckboxChange={handleCheckboxChange}
                                setIsDialogOpen={setIsDialogOpen}
                                voiceLoading={voiceLoading}
                                voices={voices}
                                voiceRef={voiceRef}
                                // handleUrlChange={handleUrlChange}
                                // formErrors={formErrors}
                                // handleVoiceChange={handleVoiceChange}
                                // handleVoiceSettingsChange={handleVoiceSettingsChange}
                                colorMode={colorMode}
                            />
                        </TabPanel>

                        {/* <TabPanel>
                            <EmbedTab
                                colorMode={colorMode}
                            />
                        </TabPanel> */}
                    </TabPanels>
                </Tabs>
            )}

            <AlertDialog
                isOpen={isDialogOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setIsDialogOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Assistant
                        </AlertDialogHeader>
                        <AlertDialogBody>Are you sure? You can't undo this action.</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                            <Button colorScheme="red" onClick={() => {
                                setIsDialogOpen(false);
                                handleDelete(selectedAssistant);
                            }} ml={3}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <Modal isOpen={isOpen} onClose={handleCallModalClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{showLiveActivity ? 'Call Me - Live Activity' : 'Call Me'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {showLiveActivity ? (
                            <LiveCallActivityTimeline
                                liveActivityUrl={liveActivityUrl}
                                onStopMonitoring={handleCallModalClose}
                            />
                        ) : (
                            <>
                                {!isRealtimeModel(formState.model) && (
                                    <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.300'} mb={4}>
                                        Live activity is available for Realtime models only.
                                    </Text>
                                )}
                                <CallSetupFields
                                    colorMode={colorMode}
                                    variables={callVariables}
                                    variableValues={variableValues}
                                    onVariableChange={handleVariableValueChange}
                                >
                                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap="10px">
                                        <FormLabel>Call From</FormLabel>
                                        <Select value={fromPhoneNumber} onChange={(e) => handleFromPhoneNumberChange(e.target.value)}>
                                            {assistantPhones.map((phone) => (
                                                <option key={phone._id} value={phone.phoneNumber}>{phone.phoneNumber}</option>
                                            ))}
                                        </Select>
                                        <FormLabel>Call To</FormLabel>
                                        <Select value={phoneNumber} onChange={(e) => handleToPhoneNumberChange(e.target.value)}>
                                            {yourPhones.map((phone) => (
                                                <option key={phone._id} value={phone.countryCode + phone.phoneNumber}>
                                                    {phone.countryCode + phone.phoneNumber}
                                                </option>
                                            ))}
                                        </Select>
                                    </Grid>
                                </CallSetupFields>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {showLiveActivity ? (
                            <Button variant="ghost" onClick={handleCallModalClose}>Close</Button>
                        ) : (
                            <>
                                <Button
                                    colorScheme="blue"
                                    onClick={handleCall}
                                    isLoading={loading}
                                >
                                    Confirm
                                </Button>
                                <Button variant="ghost" onClick={handleCallModalClose}>Cancel</Button>
                            </>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default RightPanel;
